import { css } from 'lit'
import { csColorAction, csColorBackground, csColorFunctional, csColorText } from '@cloudsuite/mosaic-shared/styles/colors.style.js'
import { csShadow } from '@cloudsuite/mosaic-shared/styles/shadows.style.js'
import { csSpacing } from '@cloudsuite/mosaic-shared/styles/spacing.style.js'
import { CSCBaseProductTile } from 'components/shared/base-product-tile/base-product-tile.style.js'

export const style = css`
  :host {
    display: block;
    max-width: 400px;
  }
  :host(.variant-wide),
  :host(.variant-inline) {
    max-width: 700px;
  }
  cse-card {
    box-shadow: ${csShadow.default};
  }
  /* Mirrors base-product-tile.style.ts's own :host([card-variant="..."]) rules (same
     var() chains and fallbacks, inlined directly — -wide-gap/-wide-padding-y/-wide-padding-x
     are legacy-fallback-only now, no longer named CSCBaseProductTile entries, so this file
     no longer imports that map at all) — this canvas renders a bare cse-card directly
     rather than csc-base-product-tile itself, so without these it would silently fall back
     to cse-card's own unrelated defaults instead of previewing the tile's actual spacing.
     variant-inline reads the real card-variant="inline" var names (-inline-gap/-inline-
     padding-y/x), not variant-wide's -wide-* ones — same as production, where "inline" and
     "wide" are genuinely separate theme-var namespaces despite rendering the same list data. */
  :host(:not(.variant-wide):not(.variant-inline)) cse-card {
    --cse-card-gap: 0; /* not themeable — see base-product-tile.style.ts */
  }
  :host(.variant-wide) cse-card {
    --cse-card-gap: var(--csc-base-product-tile-wide-gap, 0);
    --cse-card-padding-y: var(--csc-base-product-tile-wide-padding-y, 0);
    --cse-card-padding-x: var(--csc-base-product-tile-wide-padding-x, 0);
  }
  :host(.variant-inline) cse-card {
    --cse-card-gap: var(--csc-base-product-tile-inline-gap, 0);
    --cse-card-padding-y: var(--csc-base-product-tile-inline-padding-y, 0);
    --cse-card-padding-x: var(--csc-base-product-tile-inline-padding-x, 0);
  }
  /* Always zeroed, in both variants — .canvas-body-area/.canvas-footer-area (the light-DOM
     content slotted into .body/.footer) always own the visible padding themselves instead
     (effectivePaddingStyleFor in tile-configurator-canvas.ts), configured or falling back to
     the same spacing05 default cse-card would otherwise apply. That keeps those elements'
     own box exactly matching the visible padded area in every state, so they can double as
     the click/hover/selection target for the whole area — unlike base-product-tile.ts, which
     only takes over once an author actually configures a padding. */
  cse-card {
    --cse-card-body-padding-y: 0;
    --cse-card-body-padding-x: 0;
    --cse-card-footer-padding-y: 0;
    --cse-card-footer-padding-x: 0;
  }
  /* box-sizing: content-box (overriding host.style.ts's global border-box reset), specifically
     here — this element also carries its own configured padding as literal padding-* (see
     tile-configurator-canvas.ts's paddingStyleFor comment: it doubles as the image area's
     click/hover/selection target, so its own box must include the padding for its outline to
     surround the whole padded area). With the default border-box, horizontal padding on this
     same fixed-width flex-basis box would eat into the 200px instead of visibly adding space
     around it (shrinking the image rather than padding it) — content-box means padding grows
     the box outward from 200px instead, matching what production now visually does too (there,
     via cse-card's own --cse-card-header-padding-x instead of this element's own box-sizing —
     see base-product-tile.style.ts's .product-tile-header note — since it has no outline to
     keep in sync with). */
  /* variant-inline deliberately keeps the same 200px width as variant-wide here, unlike
     production's real card-variant="inline" (CSCBaseProductTile.inlineImageWidth, 100px,
     base-product-tile.style.ts) — canvas-only exception, explicitly requested: the mobile
     preview should not shrink the image column in this editor view, only the real
     csc-base-product-tile (production) renders it narrower on an actual mobile viewport. */
  :host(.variant-wide) .canvas-image-section,
  :host(.variant-inline) .canvas-image-section {
    box-sizing: content-box;
    width: 200px;
    flex: 0 0 200px;
  }
  /* cse-card's wide/list variant gives its own .body slot flex:1, but leaves
     .footer at its shrink-to-fit (flex-basis:auto) default — fine once a footer
     widget exists, but an empty orderActions zone has zero intrinsic content
     width, so .footer's auto basis collapses to ~nothing and it becomes
     undroppable. flex-grow on our own slotted content can't fix this — .footer
     lives in cse-card's shadow root, so nothing we set on our light-DOM child
     can change ITS flex-basis via flex-grow. What DOES cross the shadow
     boundary is a plain min-width: it participates in .footer's own
     content-based (shrink-to-fit) size calculation like any block content
     would. So we give every zone a real minimum footprint instead. */
  :host(.variant-wide) .canvas-body-area,
  :host(.variant-wide) .canvas-footer-area,
  :host(.variant-inline) .canvas-body-area,
  :host(.variant-inline) .canvas-footer-area {
    min-width: 96px;
  }
  /* Mirrors base-product-tile.style.ts's list-view full-height areas, so the area's selection
     outline covers the whole column and verticalAlign (inline, renderDynamicArea) has room to
     align its rows. */
  :host(.variant-wide) .canvas-body-area,
  :host(.variant-wide) .canvas-footer-area,
  :host(.variant-inline) .canvas-body-area,
  :host(.variant-inline) .canvas-footer-area {
    display: flex;
    flex-direction: column;
  }
  /* --cse-card-body-flex-grow mirrors production's :host default (base-product-tile.style.ts):
     in inline, cse-card stacks .body over .footer, and only a growing .body makes the two
     together fill the tile height. */
  :host(.variant-wide) cse-card,
  :host(.variant-inline) cse-card {
    --cse-card-footer-display: flex;
    --cse-card-body-flex-grow: 1;
  }
  :host(.variant-wide) .canvas-body-area,
  :host(.variant-inline) .canvas-body-area,
  :host(.variant-wide) .canvas-footer-area,
  :host(.variant-inline) .canvas-footer-area {
    flex: 1 1 auto;
  }
  .canvas-image-wrapper {
    --canvas-image-aspect-ratio: var(--csc-product-image-aspect-ratio, 4 / 3);
    position: relative;
    aspect-ratio: var(--canvas-image-aspect-ratio);
    overflow: hidden;
  }
  .canvas-image-wrapper csc-product-image {
    --csc-product-image-aspect-ratio: var(--canvas-image-aspect-ratio);
  }
  /* Grid has no fixed-width image column the way list's .canvas-image-section
     is pinned to 200px above — without one, this wrapper's width shrink-to-fits
     around the actual fetched picture's own intrinsic pixel size, so different
     products' real 'small' picture files (genuinely different byte dimensions,
     not CSS-scaled variants of one image) would each shrink or balloon the
     whole card differently. Pinning the width here, same idea as list's fixed
     column, keeps the canvas a consistent, workable size for every product. */
  :host(:not(.variant-wide):not(.variant-inline)) .canvas-image-wrapper {
    width: 400px;
  }
  .canvas-image-wrapper csc-product-image {
    display: block;
    overflow: hidden;
    border-radius: ${CSCBaseProductTile.imageBorderRadius};
    height: 100%;
    width: 100%;
    --csc-product-image-height-desktop: 100%;
    --csc-product-image-height-mobile: 100%;
  }
  /* The whole header area is the click/hover/selection target — matching cse-card's own
     .header, which is what actually carries the image area's padding — not just the image
     itself, so the visible dashed border includes that padding, not just the image box.
     display:block is explicit (not just relying on the <div> default) so this element's own
     block box is never left to infer its width from cse-card's shrink-to-fit sizing — an
     outline drawn right at an inferred edge is the first thing to disappear if that inference
     is ever a pixel off. A small positive outline-offset (rather than the inset -1.5px other
     area outlines use) draws it just outside the box instead, so it can never be covered by
     this section's own content. */
  .canvas-image-section {
    display: block;
    cursor: pointer;
    outline: 1.5px dashed transparent;
    outline-offset: 1px;
    border-radius: 2px;
    transition: outline-color 0.15s ease;
  }
  /* Fixed 400px, not width:100% — for the same reason wide/list mode already pins
     .canvas-image-wrapper/.canvas-image-section to a fixed size instead of a percentage:
     cse-card is display:inline-block (shrink-to-fit) in its own shared stylesheet, and
     shrink-to-fit sizing is itself derived from its content's own preferred width — including
     this element's. A percentage width here would be asking cse-card's size to depend on a
     fraction of cse-card's own (not-yet-known) size, a circular dependency browsers resolve
     inconsistently, and in practice by re-growing this box every time literal horizontal
     padding pushed its preferred width past its previous 100%, doubling the padding's visible
     effect. Pinning this to the same fixed 400px .canvas-image-wrapper (below) already uses
     removes the percentage entirely, so there's no longer any circularity for padding to feed
     back into — box-sizing:content-box (explicit here, not just relied on as the default, same
     as the wide-mode rule above) then simply grows the box outward by the configured padding,
     once, exactly like wide/list mode's own 200px already does, and cse-card (still
     shrink-to-fit) can keep growing/shrinking to match — which is the actually-wanted behavior;
     a fixed-width cse-card would fight that instead of just fixing the doubling. */
  :host(:not(.variant-wide):not(.variant-inline)) .canvas-image-section {
    box-sizing: content-box;
    width: 400px;
  }
  .canvas-image-section:hover {
    outline-color: ${csColorFunctional.highlight};
  }
  .canvas-image-section.image-selected {
    outline-color: ${csColorFunctional.highlight};
    outline-style: solid;
  }
  .canvas-image-overlay-grid {
    position: absolute;
    inset: 0;
    pointer-events: none;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
    grid-template-areas:
      "top-left top-center top-right"
      "middle-left middle-center middle-right"
      "bottom-left bottom-center bottom-right";
    gap: 4px;
    /* Matches the real csc-base-product-tile's own hardcoded corner inset —
       .image-area.* sets margin: ${csSpacing.spacing05} (10px) on each
       absolutely-positioned corner, not a themeable CSS variable, so this
       grid's outer padding mirrors that exact non-configurable value rather
       than an arbitrary editing-affordance number. */
    padding: ${csSpacing.spacing05};
  }
  /* A corner zone can hold more than one widget (e.g. two stacked badges) — its
     own internal stacking gap mirrors base-product-tile.style.ts's .image-area
     rule (not themeable, fixed at 10px), overriding the 4px this file uses
     elsewhere purely to lay out the 3x3 overlay grid itself. */
  .canvas-image-overlay-grid .canvas-zone {
    gap: ${csSpacing.spacing05};
  }
  .canvas-image-overlay-grid .canvas-zone.top-left { grid-area: top-left; }
  .canvas-image-overlay-grid .canvas-zone.top-center { grid-area: top-center; }
  .canvas-image-overlay-grid .canvas-zone.top-right { grid-area: top-right; }
  .canvas-image-overlay-grid .canvas-zone.middle-left { grid-area: middle-left; }
  .canvas-image-overlay-grid .canvas-zone.middle-right { grid-area: middle-right; }
  .canvas-image-overlay-grid .canvas-zone.bottom-left { grid-area: bottom-left; }
  .canvas-image-overlay-grid .canvas-zone.bottom-center { grid-area: bottom-center; }
  .canvas-image-overlay-grid .canvas-zone.bottom-right { grid-area: bottom-right; }
  /* Purely a full-width stretch wrapper — always exactly 1 child (renderListImageZones
     renders a single zone per slot, never splittable), so the flex/gap here have no visible
     effect beyond making that one .canvas-zone fill the width; the zone's own padding is
     applied inline on .canvas-zone itself (see renderZone's isImageFlowZone branch), not on
     this wrapper — above/below have no separate outer "area" box, unlike .canvas-body-area/
     .canvas-footer-area for productInfo/orderActions. */
  .canvas-image-flow-zone {
    display: flex;
    gap: 4px;
    width: 100%;
  }
  .canvas-image-flow-zone .canvas-zone {
    flex: 1;
    min-width: 0;
  }
  .canvas-zone {
    pointer-events: auto;
    cursor: pointer;
    /* Dashed outline stays visible once a widget is placed too — a zone's own
       boundary (as opposed to its content) is still useful to see, e.g. to
       tell where else you could drop a widget or click to select the zone. */
    border: 1.5px dashed rgba(81, 103, 140, 0.35);
    border-radius: 2px;
    transition: border-color 0.15s ease, background-color 0.15s ease;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  /* A zone's position determines how its own contents align within it — a
     top-left zone hugs the top-left corner, middle-center centers, etc. —
     matching the same alignment the real csc-base-product-tile applies.
     text-align (plus the widget-specific CSS variables some widgets expose
     instead of inheriting text-align, e.g. cse-link/cse-text) keeps multi-line
     widget text (e.g. a wrapping title) aligned the same way as the block
     itself, instead of always defaulting to the browser's left alignment. */
  .canvas-zone.top-left, .canvas-zone.top-center, .canvas-zone.top-right {
    justify-content: flex-start;
  }
  .canvas-zone.middle-left, .canvas-zone.middle-center, .canvas-zone.middle-right {
    justify-content: center;
  }
  .canvas-zone.bottom-left, .canvas-zone.bottom-center, .canvas-zone.bottom-right {
    justify-content: flex-end;
  }
  .canvas-zone.top-left, .canvas-zone.middle-left, .canvas-zone.bottom-left {
    align-items: flex-start;
    text-align: left;
    --cse-link-text-align: left;
    --cse-text-text-align: left;
  }
  .canvas-zone.top-center, .canvas-zone.middle-center, .canvas-zone.bottom-center {
    align-items: center;
    text-align: center;
    --cse-link-text-align: center;
    --cse-text-text-align: center;
  }
  .canvas-zone.top-right, .canvas-zone.middle-right, .canvas-zone.bottom-right {
    align-items: flex-end;
    text-align: right;
    --cse-link-text-align: right;
    --cse-text-text-align: right;
  }
  .canvas-zone.empty {
    min-height: 28px;
    min-width: 28px;
  }
  .canvas-zone:hover {
    border-color: ${csColorFunctional.success};
    background: rgba(23, 134, 88, 0.08);
  }
  .canvas-zone.zone-selected {
    border-style: solid;
    border-color: ${csColorFunctional.success};
    background: rgba(23, 134, 88, 0.12);
  }
  .canvas-zone.drag-over {
    border-color: ${csColorFunctional.success};
    background: rgba(23, 134, 88, 0.12);
  }
  .zone-placeholder {
    --cse-text-color: ${csColorFunctional.success};
    font-size: 14px;
    line-height: 1;
  }
  .canvas-widget-instance {
    position: relative;
    cursor: grab;
  }
  .canvas-widget-instance:active {
    cursor: grabbing;
  }
  /* The placed widget itself is a preview, never a control: its inputs, buttons and links (e.g.
     csc-order-form's stepper) would otherwise take the pointer, swap the grab cursor for a text or
     pointer cursor and look usable while doing nothing. pointer-events is inherited, so this also
     covers everything inside the widget's shadow DOM; clicks and drags land on
     .canvas-widget-instance, which selects and drags the widget. The delete button and the
     visibility dot (title tooltip) stay interactive. */
  .canvas-widget-instance > :not(.widget-remove-affordance):not(.widget-visibility-dot) {
    pointer-events: none;
  }
  /* A widget that doesn't render for the current preview product — either its showWhen rules
     don't match (.hidden-by-rules, see renderWidgetInstance) or its own built-in visibility leaves
     it empty (.empty-preview, see markEmptyWidgetPreview) — collapses into the same small dashed
     chip with "<name> · hidden for this product", so the tile reads like the real thing for this
     product while the widget stays selectable. width:fit-content because .canvas-widget-instance
     otherwise stretches to the zone's full cross-axis. Placed before .selected so a real
     selection's solid outline wins. */
  .canvas-widget-instance.empty-preview,
  .canvas-widget-instance.hidden-by-rules {
    width: fit-content;
    max-width: 100%;
    padding: ${csSpacing.spacing01} ${csSpacing.spacing03};
    outline: 1px dashed ${csColorText.body};
    outline-offset: 2px;
    opacity: 0.6;
  }
  .canvas-widget-instance.empty-preview.selected,
  .canvas-widget-instance.hidden-by-rules.selected {
    opacity: 1;
  }
  .empty-preview-label {
    display: none;
  }
  .canvas-widget-instance.empty-preview .empty-preview-label {
    display: inline;
  }
  /* .canvas-widget-instance is a flex child of its zone with the zone's own alignItems —
     'stretch' by default, matching production's .widget-instance — so unselected it correctly
     spans the full cross-axis (fidelity with what actually renders). Selected, width:fit-content
     shrinks the box (and so the outline, and .widget-remove-affordance's position — it's
     anchored to this box's own edge) to the widget's own content instead of the full stretched
     width, without touching alignItems/align-self — an explicit width always wins over stretch
     sizing for a flex item, so non-stretch zones (already content-sized) are unaffected, and
     this is purely cosmetic for the selection highlight, never exported. */
  /* A different color from .canvas-zone.zone-selected's (csColorFunctional.success) — the two
     selection levels (a whole zone vs. one widget inside it) need to read as visually distinct
     at a glance, same reasoning as areas already having their own color (csColorFunctional.
     highlight) distinct from zones — see "Selection outline styling" below. The
     .widget-remove-affordance hover color is deliberately kept in sync with this one (and
     .zone-remove-affordance's hover color with .zone-selected's), so a delete button's hover
     color alone tells you whether it belongs to a zone or a widget. */
  .canvas-widget-instance.selected {
    outline: 2px solid ${csColorAction.linkPrimary};
    outline-offset: 2px;
    border-radius: 1px;
    width: fit-content;
    max-width: 100%;
  }
  /* Exception to the fit-content above: a button widget with fit="fill" (csc-plp-cta-button,
     csc-order-form, csc-quotation-add-product) is 100% of this box, so shrinking the box on
     selection would shrink the button itself — selecting must never change how a widget
     renders. Icon-button/link variants have no width of their own, so they keep fit-content. */
  .canvas-widget-instance.selected:has(> [fit='fill']:not([variant='icon-button']):not([variant='link'])) {
    width: auto;
  }
  .canvas-widget-instance.dragging {
    opacity: 0.5;
  }
  /* The swap-target highlight while dragging one widget directly over another (see
     handleWidgetDragEnterOnWidget) — dashed and in the same green as .canvas-zone's own
     drag-over highlight, but visually distinct from .selected's solid outline so "drop here to
     swap" never reads as "already selected". */
  .canvas-widget-instance.widget-drop-target {
    outline: 2px dashed ${csColorFunctional.success};
    outline-offset: 2px;
    border-radius: 1px;
  }
  /* .canvas-body-area/.canvas-footer-area is what carries the area's padding, and is the
     click/hover/selection target for the whole area — matching cse-card's own .body/.footer,
     which is what already visibly shows padding (see effectivePaddingStyleFor). Clicking its
     own background (not a zone — zones stop propagation on their own click) selects the whole
     area, distinct from selecting a zone inside it; see tile-configurator-zone-panel.ts's area
     padding controls. */
  .canvas-body-area,
  .canvas-footer-area {
    cursor: pointer;
    outline: 1.5px dashed transparent;
    outline-offset: -1.5px;
    border-radius: 2px;
    transition: outline-color 0.15s ease;
  }
  .canvas-body-area:hover,
  .canvas-footer-area:hover {
    outline-color: ${csColorFunctional.highlight};
  }
  .canvas-body-area.area-selected,
  .canvas-footer-area.area-selected {
    outline-color: ${csColorFunctional.highlight};
    outline-style: solid;
  }
  /* Dynamic productInfo/orderActions rows: a column of rows, each row either 1 full-width
     zone or 2 zones side by side, with add-row/add-column controls capping out at
     MAX_ROWS/MAX_COLUMNS_PER_ROW (editor-state.ts). Pure layout — no padding of its own.
     gap (row gap, between stacked rows) is configurable via the sidebar's Area: .../Gap
     editor — mirrors .content's row gap in base-product-tile.style.ts; this value is only
     the unconfigured default, overridden inline per mode/area in renderDynamicArea. */
  .canvas-info-zones,
  .canvas-footer-zones {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${csSpacing.spacing05};
  }
  /* The add-row-affordance button below reserves no space while collapsed (it's out-of-flow,
     protruding past this container by --add-row-gap + its own 22px height — see
     .add-row-affordance). On hover, growing this container's own margin-bottom by that same
     amount pushes the area's padded bottom edge down to actually contain the affordance,
     instead of it overlapping whatever sits below the area (the area's own bottom padding, or
     the next area). */
  .canvas-info-zones.has-add-row-affordance,
  .canvas-footer-zones.has-add-row-affordance {
    transition: margin-bottom 0.15s ease;
  }
  .canvas-body-area:hover .canvas-info-zones.has-add-row-affordance,
  .canvas-footer-area:hover .canvas-footer-zones.has-add-row-affordance {
    margin-bottom: calc(var(--add-row-gap, 10px) + 22px);
  }
  /* gap (column gap, between the up to 2 zones in a split row) mirrors .zone-row's column
     gap in base-product-tile.style.ts — same "unconfigured default, overridden inline"
     relationship as .canvas-info-zones/.canvas-footer-zones' row gap above. */
  .canvas-zone-row {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: ${csSpacing.spacing05};
  }
  /* A stable outer wrapper (its own size never depends on being hovered) around .canvas-zone-row
     + its add-column-affordance. Hovering .canvas-zone-row *directly* — the same element whose
     own margin-right then grows in response — oscillates: hover grows the margin, the row
     shrinks out from under the cursor, hover ends, the row grows back under the cursor, hover
     restarts, forever. Same reserve-space-on-hover idea as .canvas-info-zones/.canvas-footer-
     zones' own margin-bottom (see .add-row-affordance), just with the hover trigger moved to
     the thing that doesn't resize, matching how that one triggers on .canvas-body-area/
     .canvas-footer-area (stable) rather than on .canvas-info-zones/.canvas-footer-zones
     (which is what actually grows). */
  .canvas-zone-row-wrapper {
    position: relative;
    min-width: 0;
  }
  .canvas-zone-row-wrapper.has-add-column-affordance .canvas-zone-row {
    transition: margin-right 0.15s ease;
  }
  .canvas-zone-row-wrapper.has-add-column-affordance:hover .canvas-zone-row {
    margin-right: calc(var(--add-column-gap, 10px) + 22px);
  }
  .canvas-zone-row.split .canvas-zone.dynamic-zone {
    flex: 1 1 0%;
    min-width: 0;
  }
  .canvas-zone-row.full-width .canvas-zone.dynamic-zone {
    flex: 1 1 100%;
    min-width: 0;
  }
  .canvas-zone.dynamic-zone {
    /* direction/justify-content/align-items/wrap/gap are applied inline per
       zone (see renderZone in tile-configurator-canvas.ts) once the sidebar's
       zone settings actually set a gap, overriding the default below — the
       base .canvas-zone rule's 4px gap is only a cosmetic affordance for the
       fixed image-overlay corners and doesn't apply here. Same default gap
       for productInfo/orderActions zones and list mode's above/below zones
       (renderZone adds this class for image-flow sections too, alongside
       their own place class, purely to reuse this rule) — see
       base-product-tile.style.ts's .image-above/.image-below. No min-height
       of its own — when empty it gets the same 28px every other empty zone
       (image-overlay corners) gets from the base .canvas-zone.empty rule;
       once it holds a widget, it must size to its real content + padding
       instead, or a configured padding choice wouldn't visibly change the
       zone's box (min-height would keep winning over a smaller real size). */
    position: relative;
    text-align: left;
    gap: ${csSpacing.spacing05};
  }
  /* Mirrors the "Remove zone" bin icon in the sidebar's zone-settings panel,
     but reachable directly from the canvas: only shown once the zone is actually
     selected (not on mere hover — a hover-only affordance competed visually with
     the zone's own content, and popped in/out too readily while just browsing the
     canvas), and stopPropagation()'d on click so it doesn't also trigger the
     zone's own select handler (see renderZone). */
  .zone-remove-affordance {
    position: absolute;
    top: -7px;
    right: 3px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    padding: 0;
    margin: 0;
    border: none;
    border-radius: 50%;
    background: ${csColorBackground.primary};
    color: ${csColorText.body};
    cursor: pointer;
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease;
  }
  .canvas-zone.dynamic-zone.zone-selected .zone-remove-affordance {
    opacity: 1;
    pointer-events: auto;
  }
  /* Same color as .canvas-zone.zone-selected's own outline/border — so a delete button's hover
     color alone tells you it belongs to a zone (see the note on .canvas-widget-instance.selected
     above for the widget-level counterpart). Background goes light gray rather than solid green
     on hover — the accent color shows on the icon itself instead, a lighter-touch hover than a
     solid color fill. */
  .zone-remove-affordance:hover {
    background: ${csColorBackground.secondary};
    color: ${csColorFunctional.success};
  }
  /* Same affordance as .zone-remove-affordance (selected-only, top-right corner), one level
     down — reachable directly on a widget instance instead of only via the sidebar's own
     "Delete widget" button. stopPropagation()'d on click so it doesn't also select the zone
     (see renderZone's own click handler); see renderWidgetInstance's selectHandler for why
     it additionally has to be exempted from the widget's capture-phase select handler. */
  .widget-remove-affordance {
    position: absolute;
    top: -7px;
    right: -7px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    padding: 0;
    margin: 0;
    border: none;
    border-radius: 50%;
    background: ${csColorBackground.primary};
    color: ${csColorText.body};
    cursor: pointer;
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease;
  }
  .canvas-widget-instance.selected .widget-remove-affordance {
    opacity: 1;
    pointer-events: auto;
  }
  /* Same color as .canvas-widget-instance.selected's own outline — see the note there. Same
     light-gray-background/colored-icon hover treatment as .zone-remove-affordance above. */
  .widget-remove-affordance:hover {
    background: ${csColorBackground.secondary};
    color: ${csColorAction.linkPrimary};
  }
  /* Add-row/add-column controls are hover-only overlays rather than always-visible
     buttons, so the canvas reads as a clean preview of the tile by default — the
     affordance only appears once you're pointing at the row/area you'd extend.
     add-row-affordance sits out of flow (like add-column-affordance already does),
     protruding below the container instead of being a flex item, so it — and the
     container's own gap around it — reserves no space while collapsed.
     bottom offsets by --add-row-gap (the area's real, resolved row gap — see
     renderDynamicArea) *plus* this button's own height, so it previews where a new row
     would actually land — separated from the last row by the same gap a real row would
     have — rather than sitting flush against it. */
  .add-row-affordance {
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(-1 * (var(--add-row-gap, 10px) + 22px));
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${csColorFunctional.success};
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease, background-color 0.15s ease;
  }
  .canvas-body-area:hover .add-row-affordance,
  .canvas-footer-area:hover .add-row-affordance {
    opacity: 1;
    pointer-events: auto;
    background: rgba(23, 134, 88, 0.08);
    border: 1.5px dashed ${csColorFunctional.success};
    border-radius: 2px;
  }
  /* A sibling of .canvas-zone-row inside .canvas-zone-row-wrapper (not a descendant of the row
     itself) — top/bottom: 0 stretches it to the wrapper's own height, which always equals the
     row's real height (the wrapper adds no padding/border of its own), so the button reads as
     part of the row, not a small floating square. right: 0 flush with the *wrapper's* right
     edge (the row's original, unshrunk boundary) — since .canvas-zone-row.has-add-column-
     affordance:hover's margin-right reserves exactly --add-column-gap + this button's own 22px
     width, the button (22px) plus the real gap in front of it (the remaining reserved space)
     land exactly in what that margin freed up, no separate offset math needed here. */
  .add-column-affordance {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${csColorFunctional.success};
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease, background-color 0.15s ease;
  }
  .canvas-zone-row-wrapper:hover .add-column-affordance {
    opacity: 1;
    pointer-events: auto;
    background: rgba(23, 134, 88, 0.08);
    border: 1.5px dashed ${csColorFunctional.success};
    border-radius: 2px;
  }
  /* Mobile preview is a fully inert render (no click/drag handlers are even attached — see
     tile-configurator-canvas.ts) — these just make it *look* inert too: without them the
     zone/area/image/widget elements would still show their pointer cursor and hover
     highlight, which reads as "clickable" even though nothing happens on click. */
  :host(.mobile-preview) .canvas-zone,
  :host(.mobile-preview) .canvas-image-section,
  :host(.mobile-preview) .canvas-body-area,
  :host(.mobile-preview) .canvas-footer-area,
  :host(.mobile-preview) .canvas-widget-instance {
    cursor: default;
  }
  :host(.mobile-preview) .canvas-zone:hover {
    border-color: rgba(81, 103, 140, 0.35);
    background: transparent;
  }
  :host(.mobile-preview) .canvas-image-section:hover,
  :host(.mobile-preview) .canvas-body-area:hover,
  :host(.mobile-preview) .canvas-footer-area:hover {
    outline-color: transparent;
  }
  /* Marks a widget whose visibility depends on something (see hasVisibilityConditions) — a small
     orange dot on the widget's top-left corner, the same "logic" orange as the sidebar's
     Visibility tab. */
  .widget-visibility-dot {
    position: absolute;
    top: -3px;
    left: -3px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${csColorFunctional.warning};
    pointer-events: none;
  }
  .widget-visibility-label {
    display: none;
    --cse-text-color: ${csColorText.white};
    --cs-font-caption-m-font-size: 9px;
    --cs-font-caption-m-font-size-mobile: 9px;
    --cs-font-caption-m-font-weight: 600;
    --cs-font-caption-m-line-height: 1.4;
  }
  /* Selected: the dot grows into a badge on the widget's top edge naming the rule, overlapping the
     selection outline like the remove affordance does on the opposite corner. */
  .canvas-widget-instance.selected .widget-visibility-dot {
    top: -12px;
    left: -4px;
    z-index: 2;
    width: max-content;
    height: auto;
    padding: 0 6px;
    border-radius: 3px;
    white-space: nowrap;
  }
  .canvas-widget-instance.selected .widget-visibility-label {
    display: block;
    white-space: nowrap;
  }

  /* Clean preview (hover the configurator's preview button): everything that only exists for
     editing disappears, and what production wouldn't render at all (empty zones/rows/areas, a
     widget that renders nothing) is removed from layout — so this reads as the real tile. */
  :host([clean-preview]) .canvas-zone,
  :host([clean-preview]) .canvas-zone:hover,
  :host([clean-preview]) .canvas-zone.zone-selected {
    border-color: transparent;
    background: transparent;
    transition: none;
  }
  :host([clean-preview]) .canvas-image-section,
  :host([clean-preview]) .canvas-body-area,
  :host([clean-preview]) .canvas-footer-area,
  :host([clean-preview]) .canvas-widget-instance {
    /* outline-color only — never the outline shorthand: outline:none resets the color to
       currentColor (near-black), which the areas' own outline-color transition then animates
       back from on mouse-out, flashing a black dashed outline. */
    outline-color: transparent;
    transition: none;
  }
  :host([clean-preview]) .canvas-widget-instance.selected {
    width: auto;
  }
  :host([clean-preview]) .canvas-zone.empty,
  :host([clean-preview]) .canvas-widget-instance.empty-preview,
  :host([clean-preview]) .canvas-widget-instance.hidden-by-rules,
  :host([clean-preview]) .canvas-zone-row-wrapper:not(:has(.canvas-widget-instance)),
  :host([clean-preview]) .canvas-body-area:not(:has(.canvas-widget-instance)),
  :host([clean-preview]) .canvas-footer-area:not(:has(.canvas-widget-instance)),
  :host([clean-preview]) .widget-visibility-dot,
  :host([clean-preview]) .zone-placeholder,
  :host([clean-preview]) .zone-remove-affordance,
  :host([clean-preview]) .widget-remove-affordance,
  :host([clean-preview]) .add-row-affordance,
  :host([clean-preview]) .add-column-affordance {
    display: none;
  }
`
